// src/components/common/financialReportExport.js
// Zoho-style CSV / Excel export shared by financial statements (Profit & Loss, Balance Sheet).
//
// Each row: { label, value?, level?, style, colorBySign?, blankAfter? }
//   style: "heading" | "item" | "total" | "grand" | "detail"
//   level: indentation depth (0 = left edge)
//   value: number-like; null/undefined/"" leaves the Total cell empty

const CSV_INDENT = "        ";

const GREY_TITLE = "FFEDEDED";
const GREY_ROW   = "FFF2F2F2";
const BORDER     = "FFD9D9D9";
const NUM_FMT    = "#,##0.00;-#,##0.00";

const toNum = (val) => {
  if (val === "" || val === undefined || val === null) return null;
  const num = parseFloat(val);
  if (isNaN(num)) return null;
  const rounded = Math.round(num * 100) / 100;
  return rounded === 0 ? 0 : rounded;
};

const titleLines = ({ title, basis, dateLabel }) => [
  title,
  ...(basis ? [`Basis: ${basis}`] : []),
  ...(dateLabel ? [dateLabel] : []),
];

const download = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href     = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── CSV ─────────────────────────────────────────────────────────
const csvEscape = (val) => {
  const str = String(val ?? "");
  return /[",\n]/.test(str) || /^\s/.test(str)
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

export const exportFinancialReportCsv = ({ rows, title, basis, dateLabel, filename }) => {
  const dataRows = [];
  rows.forEach((r) => {
    const num = toNum(r.value);
    dataRows.push([CSV_INDENT.repeat(r.level || 0) + r.label, num === null ? "" : String(num)]);
    if (r.blankAfter) dataRows.push(["", ""]);
  });

  const csvRows = [
    ...titleLines({ title, basis, dateLabel }).map((t) => [t]),
    [],
    ["Account", "Total"],
    ["", ""],
    ...dataRows,
  ];

  const csv = csvRows.map((r) => r.map(csvEscape).join(",")).join("\n");
  download(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), `${filename}.csv`);
};

// ── Excel ───────────────────────────────────────────────────────
const fill = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

export const exportFinancialReportXlsx = async ({
  rows,
  title,
  basis,
  dateLabel,
  filename,
  sheetName = title,
}) => {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  // Excel sheet names: max 31 chars, no []:*?/\
  const ws = wb.addWorksheet(String(sheetName).replace(/[[\]:*?/\\]/g, " ").slice(0, 31));
  ws.columns = [{ width: 55 }, { width: 20 }];

  // Title block
  const addTitleRow = (text, font, height) => {
    const r = ws.addRow([text]);
    ws.mergeCells(r.number, 1, r.number, 2);
    const c = r.getCell(1);
    c.font = font;
    c.alignment = { horizontal: "center", vertical: "middle" };
    c.fill = fill(GREY_TITLE);
    r.height = height;
  };
  addTitleRow("", {}, 10);
  titleLines({ title, basis, dateLabel }).forEach((t, i) =>
    addTitleRow(t, i === 0 ? { size: 14, bold: true } : { size: 11 }, 20)
  );
  addTitleRow("", {}, 10);

  // Column header
  const header = ws.addRow(["Account", "Total"]);
  header.height = 20;
  header.eachCell((c, col) => {
    c.font = { bold: true };
    c.fill = fill(GREY_ROW);
    c.alignment = { horizontal: col === 2 ? "right" : "left", vertical: "middle" };
    c.border = { bottom: { style: "thin", color: { argb: BORDER } } };
  });
  ws.addRow([]);

  // Data rows
  rows.forEach((r) => {
    const value = toNum(r.value);
    const row = ws.addRow([r.label, value]);
    const labelCell = row.getCell(1);
    const valueCell = row.getCell(2);
    const indent = (r.level || 0) * 2;
    valueCell.numFmt = NUM_FMT;

    switch (r.style) {
      case "heading":
        labelCell.font = { bold: true, size: r.level ? 11 : 12 };
        labelCell.alignment = { indent };
        break;

      case "detail":
        labelCell.font = { italic: true, size: 9, color: { argb: "FF808080" } };
        labelCell.alignment = { indent };
        break;

      case "total":
        row.height = 20;
        [labelCell, valueCell].forEach((c) => {
          c.font = { bold: true, size: 12 };
          c.fill = fill(GREY_ROW);
          c.border = { top: { style: "thin", color: { argb: BORDER } } };
        });
        labelCell.alignment = { indent, vertical: "middle" };
        valueCell.alignment = { vertical: "middle" };
        break;

      case "grand": {
        row.height = 22;
        [labelCell, valueCell].forEach((c) => {
          c.font = { bold: true, size: 12 };
          c.fill = fill(GREY_TITLE);
          c.border = {
            top: { style: "thin", color: { argb: BORDER } },
            bottom: { style: "double", color: { argb: BORDER } },
          };
        });
        labelCell.alignment = { indent, vertical: "middle" };
        valueCell.alignment = { vertical: "middle" };
        if (r.colorBySign) {
          valueCell.font = {
            bold: true,
            size: 12,
            color: { argb: (value ?? 0) < 0 ? "FFC00000" : "FF007A33" },
          };
        }
        break;
      }

      case "item":
      default:
        labelCell.alignment = { indent };
        break;
    }

    if (r.blankAfter) ws.addRow([]);
  });

  const buffer = await wb.xlsx.writeBuffer();
  download(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${filename}.xlsx`
  );
};
